/**
 * @namespace Tabs
 * @memberof Promega.Components
 * @description Methods for programmatically controlling Tab components. See example for the types of markup to use
 * in order for the functionality to be registered automatically.
 * 
 * @example <caption>A tab component contains a set of tab buttons (what you click to switch tabs) and tab contents
 * (what you actually see). Tab buttons are associated with the appropriate contents by giving them the same value
 * for attribute <code>data-tab-name</code>.<br><br>
 * Each tab button must have class <code>tabs__button</code>, and each content area must have class <code>tabs__content</code>.<br><br>
 * Additional classes on the tabs component, its header, button, and its content areas are managed by the tabs component internally
 * when showing and hiding individual tabs.</caption>
 * <div class="content-container--tabs">
 *   <div class="tabs__header">
 *     <div class="tabs__button tabs__button--active" data-tab-name="first-tab">The first tab</div>
 *     <div class="tabs__button" data-tab-name="second-tab">The second tab</div>
 *   </div>
 *   <div class="tabs__content tabs__content--visible" data-tab-name="first-tab">The content of the first tab goes here.</div>
 *   <div class="tabs__content" data-tab-name="second-tab">The content of the second tab goes here.</div>
 * </div>
 */

import addOrReplaceQueryStringParameter from '../util/addOrReplaceQueryStringParameter';
import trackEvent from '../util/trackEvent';

const CONTAINER_CLASS = "content-container--tabs",
    TABS_BUTTON_CLASS = "tabs__button",
    TABS_BUTTON_ACTIVE_CLASS = "tabs__button--active",
    TABS_CONTENT_CLASS = "tabs__content",
    TABS_CONTENT_VISIBLE_CLASS = "tabs__content--visible",
    MOBILE_DROPDOWN_CLASS = "tabs__mobile-dropdown";
    
let mutationObserver = null;

// Private functions/listeners

function switchTabs(event) {
    if (event && event.target) {
        let isTabEvent = true,
            tabName = "",
            eventSource = null;
        if (event.type === "click" && event.target.classList.contains(TABS_BUTTON_CLASS)) {
            tabName = event.target.getAttribute("data-tab-name");
            eventSource = "Normal";
        }
        else if (event.type === "change" && event.target.classList.contains(MOBILE_DROPDOWN_CLASS)) {
            tabName = event.target.value;
            eventSource = "Dropdown";
        }
        else isTabEvent = false;

        if (isTabEvent) {
            event.preventDefault();
            const tabParent = event.target.closest(`.${CONTAINER_CLASS}`);
            if (tabParent) {
                showTab(tabName, tabParent);
                trackEvent("Components", "Tabs - switched tabs", eventSource);
            }
        }
    }
}

function updateTabQueryString () {
    let allTabContainers = document.getElementsByClassName(CONTAINER_CLASS),
        containerCount = allTabContainers.length;
    for (let i=0; i<containerCount; i++) {
        let currentContainer = allTabContainers[i],
            lastActiveTabName = currentContainer.getAttribute("data-last-active-tab"),
            lastTabIndex = getIndexOfTab(lastActiveTabName, currentContainer);
        if (history && history.replaceState) {
            history.replaceState(null, null, addOrReplaceQueryStringParameter(window.location, "tabset" + i, lastTabIndex));
        }
    }
}

function generateMobileButtons (tabContainer) {
    const allWideButtons = tabContainer.getElementsByClassName(TABS_BUTTON_CLASS),
        mobileDropdown = tabContainer.getElementsByClassName(MOBILE_DROPDOWN_CLASS)[0],
        allDropdownOptions = mobileDropdown.getElementsByTagName("option");

    if (mobileDropdown && allDropdownOptions.length === 0) {
        for (let i = 0; i < allWideButtons.length; i++) {
            const newOption = document.createElement("option");

            newOption.setAttribute("value", allWideButtons[i].getAttribute("data-tab-name"));
            newOption.innerText = allWideButtons[i].innerText;
            if (allWideButtons[i].classList.contains(TABS_BUTTON_ACTIVE_CLASS)) newOption.selected = true;
            mobileDropdown.appendChild(newOption);
        }
    }
}

function throwContainerDoesNotExistException (tabContainer) {
    throw "Tab component \"" + tabContainer + "\" does not exist.";
}

// API

/**
 * @function showTab
 * @memberof Promega.Components.Tabs
 * @description Shows the tab in the given tab component.
 * @param {String} tabName The name of the tab to show
 * @param {HTMLElement} tabContainer An element representing the entire tab component
 * @param {JSON} [options]
 * @param {Boolean} options.skipQueryStringUpdate If true, prevents the browser's query string from being updated
 */
export function showTab (tabName, tabContainer, options) {
    if (tabContainer) {
        let tabContentToShow = Array.from(tabContainer.querySelectorAll("." + TABS_CONTENT_CLASS))
            .find(el => el.getAttribute('data-tab-name')?.toLowerCase() === tabName.toLowerCase() ||
                el.getAttribute('data-tab-title')?.toLowerCase() === tabName.toLowerCase());
        let tabButtonToActivate = Array.from(tabContainer.querySelectorAll("." + TABS_BUTTON_CLASS))
            .find(el => el.getAttribute('data-tab-name')?.toLowerCase() === tabName.toLowerCase() ||
                el.getAttribute('data-tab-other')?.toLowerCase() === tabName.toLowerCase());
        let mobileDropdown = tabContainer.getElementsByClassName(MOBILE_DROPDOWN_CLASS)[0];
        if (tabContentToShow && tabButtonToActivate) {
            hideActiveTab(tabContainer);
            tabContentToShow.classList.add(TABS_CONTENT_VISIBLE_CLASS);
            tabButtonToActivate.classList.add(TABS_BUTTON_ACTIVE_CLASS);
            tabContainer.setAttribute("data-last-active-tab", tabName);
            mobileDropdown.value = tabName;

            //scroll into view if product page tabs are specified
            if (tabName == 'specifications' || tabName == 'protocols' || tabName == 'overview' || tabName == 'resources' || tabName == 'related-products')
                tabButtonToActivate.scrollIntoView(true);

            if (!(options && options.skipQueryStringUpdate)) updateTabQueryString();
        }
    }
    else throwContainerDoesNotExistException();
}

/**
 * @function hideTab
 * @memberof Promega.Components.Tabs
 * @description Hides the tab in the given tab component.
 * @param {String} tabName The name of the tab to show
 * @param {HTMLElement} tabContainer An element representing the entire tab component
 */
export function hideTab (tabName, tabContainer) {
    if (tabContainer) {
        let tabContentToHide = tabContainer.querySelector("." + TABS_CONTENT_CLASS + "[data-tab-name='" + tabName + "']"),
            tabButtonToDeactivectivate = tabContainer.querySelector("." + TABS_BUTTON_CLASS + "[data-tab-name='" + tabName + "']");
        if (tabContentToHide && tabButtonToDeactivectivate) {
            tabContentToHide.classList.remove(TABS_CONTENT_VISIBLE_CLASS);
            tabButtonToDeactivectivate.classList.remove(TABS_BUTTON_ACTIVE_CLASS);
        }
    }
    else throwContainerDoesNotExistException();
}

/**
 * @function hideActiveTab
 * @memberof Promega.Components.Tabs
 * @description Determines which tab is currently visible within the given tab component and hides it.
 * @param {HTMLElement} tabContainer An element representing the entire tab component
 */
export function hideActiveTab (tabContainer) {
    if (tabContainer) {
        let tabToHide = tabContainer.querySelector(":scope > ." + TABS_CONTENT_VISIBLE_CLASS);
        if (tabToHide) hideTab(tabToHide.getAttribute("data-tab-name"), tabContainer);
    }
    else throwContainerDoesNotExistException();
}

/**
 * @function isTabVisible
 * @memberof Promega.Components.Tabs
 * @description Determines whether or not the tab within the given tab container is currently visible.
 * @param {String} tabName The name of the tab to show
 * @param {HTMLElement} tabContainer An element representing the entire tab component
 * @returns {Boolean}
 */
export function isTabVisible (tabName, tabContainer) {
    if (tabContainer) {
        let tabToCheck = tabContainer.querySelector("." + TABS_CONTENT_CLASS + "[data-tab-name='" + tabName + "']");
        if (tabToCheck) {
            return tabToCheck.classList.contains(TABS_CONTENT_VISIBLE_CLASS);
        }
        else throw "Unable to hide tab \"" + tabName + "\" - tab does not exist.";
    }
    else throwContainerDoesNotExistException();
}

/**
 * @function getIndexOfTab
 * @memberof Promega.Components.Tabs
 * @description Gets the zero-based index of the tab within the given tab container.
 * @param {String} tabName The name of the tab to show
 * @param {HTMLElement} tabContainer An element representing the entire tab component
 * @returns {Number}
 */
export function getIndexOfTab (tabName, tabContainer) {
    let tabElement = tabContainer.querySelector("." + TABS_CONTENT_CLASS + "[data-tab-name='" + tabName + "']"),
        allTabsInContainer = tabContainer.querySelectorAll(":scope > ." + TABS_CONTENT_CLASS),
        tabCount = allTabsInContainer.length,
        tabIndex = -1;
    for (let i=0; i<tabCount; i++) {
        if (tabElement === allTabsInContainer[i]) {
            tabIndex = i;
            break;
        }
    }
    return tabIndex;
}

/**
 * @function getNameOfTab
 * @memberof Promega.Components.Tabs
 * @description Gets the name of the tab with the given zero-based index within the given tab container.
 * @param {Number} tabIndex The zero-based index of the tab
 * @param {Element} tabContainer An element representing the entire tab component
 * @returns {String}
 */
export function getNameOfTab(tabIndex, tabContainer) {
    let allTabsInContainer = tabContainer.querySelectorAll(":scope > ." + TABS_CONTENT_CLASS),
        tabAtIndex = allTabsInContainer[tabIndex];
    if (tabAtIndex) return tabAtIndex.getAttribute("data-tab-name");
}

const getTabsetsFromUrl = (url) => {
    const queryString = new URL(url).searchParams;
    const tabsets = [];

    for (const [key, value] of queryString.entries()) {
        if (key.startsWith("tabset")) {
            tabsets.push({ key, value });
        }
    }
    return tabsets;
};

const setInitialTabState = (urlTabsets, pageTabsets) => {
    if (pageTabsets.length > 0 && urlTabsets.length > 0) {
        for (let i = 0, j = pageTabsets.length; i < j; i++) {
            let currentTabName;
            if (!/\d/.test(urlTabsets[i].value)) {
                currentTabName = urlTabsets[i].value;
            } else {
                currentTabName = getNameOfTab(parseInt(urlTabsets[i].value), pageTabsets[i]);
            }
            if (!currentTabName) {
                const currentTabName = getNameOfTab(0, pageTabsets[i]);
                showTab(currentTabName, pageTabsets[i], { skipQueryStringUpdate: false });
                continue;
          }
            showTab(currentTabName, pageTabsets[i], { skipQueryStringUpdate: true });
        }
    } else if (pageTabsets.length > 0) {
        for (let i = 0, j = pageTabsets.length; i < j; i++) {
            const currentTabName = getNameOfTab(0, pageTabsets[i]);
            if (!currentTabName) {
                continue;
            }
            showTab(currentTabName, pageTabsets[i], { skipQueryStringUpdate: false });
        }
    }
};

// Init code

{
    document.addEventListener("click", switchTabs, true);
    document.addEventListener("change", switchTabs, true);

    // Generate the mobile buttons and detect the starting tab, if applicable
    let allTabContainers = document.getElementsByClassName(CONTAINER_CLASS),
        containerCount = allTabContainers.length;
    const urlTabsets = getTabsetsFromUrl(window.location.href);
    for (let i = 0; i < containerCount; i++) {
        generateMobileButtons(allTabContainers[i]);
    }
    setInitialTabState(urlTabsets, allTabContainers);


    // Observe the DOM for changes. When a new tab component is inserted, generate mobile buttons for it. This is really only relevant in the Experience Editor.
    if (typeof MutationObserver !== "undefined") {
        mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList") {
                    Array.prototype.forEach.call(mutation.addedNodes, newNode => {
                        if (newNode.nodeType === Node.ELEMENT_NODE && newNode.nodeName.toLowerCase() !== "svg") {
                            const containerList = newNode.getElementsByClassName(CONTAINER_CLASS);
                            for (let i = 0; i < containerList.length; i++) generateMobileButtons(containerList[i]);
                        }
                    });
                }
            });
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });
    }
    else {
        // Browser does not support MutationObserver. Poll all tab containers periodically to see if mobile buttons need to be generated.
        setInterval(() => {
            const containerList = document.getElementsByClassName(CONTAINER_CLASS);
            for (let i = 0; i < containerList.length; i++) generateMobileButtons(containerList[i]);
        }, 1000);
    }
}
